import type { ProspectResponse } from '@common/types/prospects';
import type { QuoteTemplateResponse } from '@common/types/quoteTemplates';
import { Box, Divider, Grid, Typography } from '@mui/material';
import QuoteItem from '@/components/Email/QuoteItem';
import { formatCurrency, parseCurrency } from '@/utils';

interface ManualQuoteFieldType {
    label: string;
    value: string | number | null;
    deductions: boolean;
}

interface Props {
    prospect: ProspectResponse;
    quoteResp?: QuoteTemplateResponse;
}

export default function SendQuoteManuallyQuoteStructure({ prospect, quoteResp }: Props) {
    const { prospectSummary } = prospect;
    const isAlberta = quoteResp?.province === 'Alberta';
    const transactionTypesToShow = ['Purchase', 'Refinance'];
    const isTransactionTypeToShow = transactionTypesToShow.includes(prospect.matterType);
    const isPurchase = prospect.matterType === 'Purchase';
    const discount = parseFloat(`${parseCurrency(`${prospectSummary.legalFeeDiscount}`)}`) || 0;
    const netLegalFee = parseFloat(`${parseCurrency(`${prospectSummary.legalFee}`)}`) - discount;

    const quotingFieldsSingle: ManualQuoteFieldType[] = [];

    quotingFieldsSingle.push({
        label: 'Legal fee before tax',
        value: (parseFloat(`${parseCurrency(`${prospectSummary.legalFee}`)}`) || 0).toFixed(2),
        deductions: false,
    });
    if (discount > 0) {
        quotingFieldsSingle.push({
            label: 'Discount on Legal Fee',
            value: discount.toFixed(2),
            deductions: false,
        });
        quotingFieldsSingle.push({
            label: 'Net legal fee',
            value: netLegalFee.toFixed(2),
            deductions: false,
        });
    }
    quotingFieldsSingle.push({
        label: `${isAlberta ? 'GST' : 'HST'} on legal fee`,
        value: (Number(prospectSummary.legalFeeWithHst) - netLegalFee || 0).toFixed(2),
        deductions: false,
    });

    quotingFieldsSingle.push({
        label: `Estimated disbursements with ${isAlberta ? 'GST' : 'HST'}`,
        value: parseCurrency(`${prospectSummary.disbursementWithHst}`),
        deductions: false,
    });
    if (prospectSummary.estimatedRegistrationFees) {
        quotingFieldsSingle.push({
            label: 'Estimated registration fees',
            value: prospectSummary.estimatedRegistrationFees,
            deductions: false,
        });
    }
    if (isTransactionTypeToShow) {
        quotingFieldsSingle.push({
            label: 'Estimated title insurance fee',
            value: prospectSummary.titleInsurancePrice,
            deductions: false,
        });
    }
    if (isPurchase && !isAlberta) {
        quotingFieldsSingle.push({
            label: 'Land transfer tax',
            value: formatCurrency(prospectSummary.landTransferTax),
            deductions: true,
        });
        quotingFieldsSingle.push({
            label: 'First time home buyer rebate',
            value: formatCurrency(prospectSummary.landTransferTaxRebate),
            deductions: true,
        });
        quotingFieldsSingle.push({
            label: 'Net Land Transfer Tax',
            value: prospectSummary.netLandTransferTax,
            deductions: false,
        });
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                gap: '12px',
                marginTop: '16px',
            }}
        >
            {quotingFieldsSingle.map((field) => {
                if (field.deductions) {
                    return (
                        <Grid container key={field.label}>
                            <Grid item sm={8}>
                                <Typography variant="body2" display="inline-block">
                                    {field.label}
                                </Typography>
                            </Grid>
                            <Grid item sm={4}>
                                <Typography variant="labelMedium" display="inline-block">
                                    {field.label === 'Discount on Legal Fee'
                                        ? `-$${field.value}`
                                        : `${field.value}`}
                                </Typography>
                            </Grid>
                        </Grid>
                    );
                }
                return (
                    <QuoteItem
                        key={field.label}
                        itemLabel={field.label}
                        value={field.value}
                        formatAsCurrency
                    />
                );
            })}
            <Box
                sx={{
                    width: '100%',
                    minHeight: '1px',
                }}
            >
                <Divider orientation="horizontal" sx={{ margin: '8px 0px' }} />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Quote total:
                </Typography>
                <Typography variant="h3">
                    {`$${formatCurrency((prospect?.quoteTotal || 0).toFixed(2))}`}
                </Typography>
            </Box>
        </Box>
    );
}
